// Port of references/claude-design-source/project/src/virtual-table.jsx.
// Pure windowed virtualization. ALL rows are considered loaded; the hook
// only decides which slice is in the DOM right now.
//
// Layout contract (CSS-side): the parent `<div class="tbl-wrap vt">` provides
// --vt-cols, --vt-row-h, --vt-body-h; <thead> and each body <tr> use that
// template; body <tr> are position:absolute + transform:translateY(i*rowH).
//
// THE RULE THIS FILE EXISTS TO ENFORCE
// ------------------------------------
// A hook that measures a DOM node must OWN that node's ref and hand back a
// callback ref. Never accept a `RefObject` across a hook boundary, and never
// list one in a dependency array: a ref object is allocated once and never
// replaced, so `.current` going null → <div> is invisible to React by
// construction. Listing it is a no-op that reads like a dependency.
//
// That was this file's bug, and it cost a blank `/campaigns` table. The effect
// that measures the container bailed on `scrollRef.current === null` while the
// screen was showing a loading placeholder, and `[.., scrollRef]` could never
// re-run it. On a hard reload the rows and the container happened to arrive in
// the same commit, so `rows.length` changed and it recovered — which is exactly
// why it looked like a data-loading problem and was not one.

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

interface VtCfg {
  overscan: number;
}

declare global {
  interface Window {
    __VT_CFG?: VtCfg;
  }
}

// Global overscan (rows above + below visible window).
if (typeof window !== 'undefined' && !window.__VT_CFG) {
  window.__VT_CFG = { overscan: 40 };
}

export function setVtCfg(patch: Partial<VtCfg>) {
  Object.assign(window.__VT_CFG!, patch);
  window.dispatchEvent(new CustomEvent('vt-cfg-change'));
}

export function useVtCfg(): VtCfg {
  const [, setV] = useState(0);
  useEffect(() => {
    const h = () => setV((x) => x + 1);
    window.addEventListener('vt-cfg-change', h);
    return () => window.removeEventListener('vt-cfg-change', h);
  }, []);
  return (typeof window !== 'undefined' ? window.__VT_CFG : undefined) ?? { overscan: 40 };
}

interface RowLike {
  id: string | number;
}

/** The DOM facts a virtualizer needs. Separated so a test can supply them. */
export interface VtViewport {
  scrollTop: number;
  clientHeight: number;
  clientWidth: number;
  paddingX: number;
}

/**
 * Injectable measurement seam. jsdom reports every box as 0×0, so a test that
 * needs a real viewport passes its own `measure` instead of stubbing prototype
 * getters on the element.
 */
export type VtMeasure = (el: HTMLElement) => VtViewport;

export const measureDom: VtMeasure = (el) => {
  const cs = getComputedStyle(el);
  return {
    scrollTop: el.scrollTop,
    clientHeight: el.clientHeight,
    clientWidth: el.clientWidth,
    paddingX: (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0),
  };
};

/**
 * A DOM node whose ATTACHMENT is observable.
 *
 * Holding the node in state makes attachment a real state transition, so no
 * caller's render order can hide it — there is no order in which a mounted node
 * fails to produce a commit. React invokes ref callbacks during the commit
 * phase, so the resulting update flushes before paint: one extra render, no
 * flash.
 */
export function useAttachedElement<E extends HTMLElement>(): readonly [
  E | null,
  (node: E | null) => void,
] {
  const [el, setEl] = useState<E | null>(null);
  // Stable identity. React re-invokes a ref callback whenever its identity
  // changes, so an inline arrow would detach and reattach on every render.
  const ref = useCallback((node: E | null) => {
    setEl(node);
  }, []);
  return [el, ref] as const;
}

export interface VtRange {
  start: number;
  end: number;
}

/** No DOM, no React — unit-testable in the plain node environment. */
export function computeRowRange(a: {
  scrollTop: number;
  clientHeight: number;
  rowH: number;
  overscan: number;
  total: number;
}): VtRange {
  const start = Math.max(0, Math.floor(a.scrollTop / a.rowH) - a.overscan);
  const end = Math.min(a.total, Math.ceil((a.scrollTop + a.clientHeight) / a.rowH) + a.overscan);
  return { start, end: Math.max(start, end) };
}

/** Dev-only: rows to virtualize but nothing ever attached. Names the mistake. */
function useUnattachedTripwire(el: HTMLElement | null, total: number) {
  useEffect(() => {
    if (!import.meta.env.DEV || el || total === 0) return;
    const t = setTimeout(() => {
      console.error(
        '[virtual-table] %d rows are being virtualized but no container has ' +
          'attached after a second. Did you forget ref={containerRef}, or is the ' +
          'element behind an early return? The body will render blank.',
        total,
      );
    }, 1000);
    return () => clearTimeout(t);
  }, [el, total]);
}

interface UseVirtualRowsArgs<T extends RowLike> {
  rows: ReadonlyArray<T>;
  rowH: number;
  overscan?: number;
  /** Test seam. Must be module-level or otherwise referentially stable. */
  measure?: VtMeasure;
}

export function useVirtualRows<T extends RowLike>({
  rows,
  rowH,
  overscan,
  measure = measureDom,
}: UseVirtualRowsArgs<T>) {
  const cfg = useVtCfg();
  const buffer = overscan ?? cfg.overscan ?? 40;
  const [el, containerRef] = useAttachedElement<HTMLElement>();
  const [range, setRange] = useState<VtRange>({ start: 0, end: 0 });
  const total = rows.length;

  // Read through a ref so an inline `measure` cannot thrash the effect.
  const measureRef = useRef(measure);
  measureRef.current = measure;

  // Reset scroll to top when the row LIST changes (filter/sort) — but skip
  // the very first mount (no prior list to compare to). Keyed on `el`, so it
  // is correct whichever commit attached the container.
  const prevSigRef = useRef<string | null>(null);
  const sig = useMemo(
    () => total + ':' + (rows[0]?.id ?? '') + ':' + (rows[total - 1]?.id ?? ''),
    [rows, total],
  );
  useEffect(() => {
    if (prevSigRef.current !== null && prevSigRef.current !== sig && el) {
      el.scrollTop = 0;
    }
    prevSigRef.current = sig;
  }, [sig, el]);

  // Recompute the visible range on scroll AND on container resize. Driven by
  // rAF so we never update state more than once per frame; only updates state
  // when the slice actually moves so React doesn't re-render uselessly.
  //
  // The ResizeObserver callback only SCHEDULES the measurement. Measuring
  // inside it (getComputedStyle, clientHeight) forces a layout in the middle of
  // the browser's observation loop; when the container's size moved since the
  // observation was gathered — the loading placeholder giving way to rows, a
  // drawer narrowing the list — WebKit then finds the same element resized at a
  // depth it has already delivered and reports "ResizeObserver loop completed
  // with undelivered notifications" as a page error (the 2026-10 route crawl,
  // ~50% of loads at 834 px). Deferred to the next frame, the read happens after
  // the loop, against settled layout.
  useLayoutEffect(() => {
    if (!el) {
      // Not yet attached, or detached. Drop any stale window so a remount
      // cannot paint rows measured against a dead element.
      setRange((prev) => (prev.start === 0 && prev.end === 0 ? prev : { start: 0, end: 0 }));
      return;
    }
    let raf = 0;
    const recompute = () => {
      const { scrollTop, clientHeight } = measureRef.current(el);
      const next = computeRowRange({ scrollTop, clientHeight, rowH, overscan: buffer, total });
      setRange((prev) => (prev.start === next.start && prev.end === next.end ? prev : next));
    };
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(recompute);
    };
    recompute();
    el.addEventListener('scroll', schedule, { passive: true });
    // Schedule only — never measure inside the observer callback (above).
    const ro = new ResizeObserver(schedule);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', schedule);
      ro.disconnect();
      cancelAnimationFrame(raf);
    };
    // Every entry is a primitive or a real node — never a ref object.
  }, [el, total, rowH, buffer]);

  useUnattachedTripwire(el, total);

  const visible = useMemo(
    () => rows.slice(range.start, range.end),
    [rows, range.start, range.end],
  );

  return {
    rows,
    visible,
    startIdx: range.start,
    totalH: total * rowH,
    total,
    /** Attach with `<div ref={containerRef}>`. The only way in. */
    containerRef,
  };
}

export interface VtGridGeometry extends VtRange {
  cols: number;
  cardW: number;
  /** The card height this geometry was laid out with — a caller's function of `cardW` is resolved here. */
  cardH: number;
  rowH: number;
  totalH: number;
}

/**
 * A card height is either fixed or a function of the card width the grid
 * settles on. The Creative Library's poster is 4:3 of a fluid column, so a
 * fixed 270px slot clipped every card whose caption ran to a second line
 * once the columns grew past ~230px (prod, 2026-09-18: three cards in four).
 */
export type VtCardHeight = number | ((cardW: number) => number);

/**
 * Columns, card width and the visible range as ONE value.
 *
 * They used to be three: `w` was state, `cols` was derived during render, and
 * the range was set inside the effect from the `cols` captured when the effect
 * was created. Since `w` starts at 0, `cols` starts at 1, so the first
 * committed range sliced the items as a single column while the caller
 * positioned those cards using the new `cols` — one frame of wrong grid, with
 * `totalH` briefly many times too tall. Committing them together makes
 * disagreement unrepresentable.
 */
export function computeGridRange(a: {
  scrollTop: number;
  clientHeight: number;
  contentWidth: number;
  cardMinW: number;
  cardH: VtCardHeight;
  gap: number;
  overscan: number;
  total: number;
}): VtGridGeometry {
  const cols = Math.max(1, Math.floor((a.contentWidth + a.gap) / (a.cardMinW + a.gap)));
  const cardW =
    a.contentWidth > 0 ? Math.floor((a.contentWidth - (cols - 1) * a.gap) / cols) : a.cardMinW;
  const cardH = Math.max(1, Math.round(typeof a.cardH === 'function' ? a.cardH(cardW) : a.cardH));
  const rowH = cardH + a.gap;
  const rowCount = Math.ceil(a.total / cols);
  const startRow = Math.max(0, Math.floor(a.scrollTop / rowH) - a.overscan);
  const endRow = Math.min(rowCount, Math.ceil((a.scrollTop + a.clientHeight) / rowH) + a.overscan);
  const start = startRow * cols;
  return {
    cols,
    cardW,
    cardH,
    rowH,
    totalH: rowCount * rowH,
    start,
    end: Math.max(start, Math.min(a.total, endRow * cols)),
  };
}

interface UseVirtualGridArgs<T extends RowLike> {
  items: ReadonlyArray<T>;
  cardMinW?: number;
  /** Fixed, or derived from the settled card width. Pass a module-level function so the effect's deps stay stable. */
  cardH?: VtCardHeight;
  gap?: number;
  overscan?: number;
  measure?: VtMeasure;
}

export function useVirtualGrid<T extends RowLike>({
  items,
  cardMinW = 220,
  cardH = 200,
  gap = 12,
  overscan = 2,
  measure = measureDom,
}: UseVirtualGridArgs<T>) {
  const [el, containerRef] = useAttachedElement<HTMLElement>();
  const total = items.length;
  const [geom, setGeom] = useState<VtGridGeometry>(() =>
    computeGridRange({
      scrollTop: 0,
      clientHeight: 0,
      contentWidth: 0,
      cardMinW,
      cardH,
      gap,
      overscan,
      total,
    }),
  );

  const measureRef = useRef(measure);
  measureRef.current = measure;

  const prevSigRef = useRef<string | null>(null);
  const sig = total + ':' + (items[0]?.id ?? '') + ':' + (items[total - 1]?.id ?? '');
  useEffect(() => {
    if (prevSigRef.current !== null && prevSigRef.current !== sig && el) {
      el.scrollTop = 0;
    }
    prevSigRef.current = sig;
  }, [sig, el]);

  useLayoutEffect(() => {
    if (!el) return;
    let raf = 0;
    const recompute = () => {
      const { scrollTop, clientHeight, clientWidth, paddingX } = measureRef.current(el);
      const next = computeGridRange({
        scrollTop,
        clientHeight,
        contentWidth: clientWidth - paddingX,
        cardMinW,
        cardH,
        gap,
        overscan,
        total,
      });
      setGeom((prev) =>
        prev.start === next.start &&
        prev.end === next.end &&
        prev.cols === next.cols &&
        prev.cardW === next.cardW &&
        prev.cardH === next.cardH &&
        prev.totalH === next.totalH
          ? prev
          : next,
      );
    };
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(recompute);
    };
    recompute();
    el.addEventListener('scroll', schedule, { passive: true });
    // Never measure inside the observer callback: see useVirtualRows.
    const ro = new ResizeObserver(schedule);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', schedule);
      ro.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [el, total, cardMinW, cardH, gap, overscan]);

  useUnattachedTripwire(el, total);

  const visible = useMemo(
    () => items.slice(geom.start, geom.end),
    [items, geom.start, geom.end],
  );

  return {
    items,
    visible,
    startIdx: geom.start,
    cols: geom.cols,
    cardW: geom.cardW,
    cardH: geom.cardH,
    gap,
    totalH: geom.totalH,
    rowH: geom.rowH,
    /** Attach with `<div ref={containerRef}>`. The only way in. */
    containerRef,
  };
}

// Build a CSS grid-template-columns string from minimum widths.
//
// Numbers → `minmax(Npx, Nfr)` so the column has a hard floor and a
// proportional growth share equal to N. Strings (e.g. '36px') pass through
// unchanged for columns that should never grow.
export function vtCols(...mins: Array<number | string>): string {
  return mins.map((m) => (typeof m === 'string' ? m : `minmax(${m}px, ${m}fr)`)).join(' ');
}
